<template>
  <BittsModal
    container-class="c-population-sharing-modal-container"
    primary-button-text="Save"
    secondary-button-text="Cancel"
    :width="920"
    :disabled="!hasChanges"
    :action-loading="isSaving"
    :show-close-button="false"
    @closed="emit('close')"
    @action="handleSave"
    @secondary-action="emit('close')"
  >
    <template #header>
      <div class="flex w-full items-center justify-between">
        <h1 class="text-neutral-text-strong font-bold mr-16 text-lg">
          {{ modalTitle }}
        </h1>
        <BittsButton
          aria-label="Close"
          :center-icon="['fak', 'x']"
          @click="emit('close')"
          variant="ghost"
          type="neutral"
        />
      </div>
    </template>
    <BittsLoading :is-loading="isLoading">
      <div class="c-population-sharing-modal__wrapper">
        <div class="c-population-sharing-modal">
          <BittsAlert
            v-if="shouldShowAlert"
            :message="alertMessage"
            :description="alertDescription"
            color="info"
            class="c-population-sharing-modal__alert"
          />
          <!-- Selection Section (outside scroll container so dropdown isn't clipped) -->
          <div
            v-if="isMultiSelect"
            class="c-population-sharing-modal__selection-section"
          >
            <div class="c-population-sharing-modal__section">
              <div class="c-population-sharing-modal__section-left">
                <h3 class="c-population-sharing-modal__section-title">
                  {{
                    isForPartner ? 'Selected Partners' : 'Selected Populations'
                  }}
                </h3>
                <p class="c-population-sharing-modal__section-description">
                  Choose the
                  {{ isForPartner ? 'partners' : 'populations' }} you want to
                  update. Changes will apply to all selected
                  {{ isForPartner ? 'partners' : 'populations' }}.
                </p>
              </div>
              <div class="c-population-sharing-modal__section-right">
                <BittsFilterMultiselect
                  v-if="isForPartner"
                  v-model="selectedPartnerIds"
                  :options="allPartnerOptions"
                  placeholder="Select partners"
                  search-placeholder="Search partners"
                  :use-search="true"
                  :use-overflow="true"
                  :mount-to-body="false"
                  :match-trigger-width="true"
                  class="c-population-sharing-modal__filter-multiselect"
                >
                  <template #tagPrefix="{ item }">
                    <BittsAvatar
                      size="x-small"
                      shape="square"
                      :org="item.org"
                      :show-initials="true"
                    />
                  </template>
                  <template #prefix="{ option }">
                    <BittsAvatar
                      size="x-small"
                      shape="square"
                      :org="option.org"
                      :show-initials="true"
                    />
                  </template>
                </BittsFilterMultiselect>
                <BittsFilterMultiselect
                  v-else
                  v-model="selectedPopulationIds"
                  :options="allPopulationOptions"
                  placeholder="Select populations"
                  search-placeholder="Search populations"
                  :use-search="true"
                  :use-overflow="true"
                  :show-group-checkbox="true"
                  :mount-to-body="false"
                  :match-trigger-width="true"
                  class="c-population-sharing-modal__filter-multiselect"
                >
                  <template #tagPrefix="{ item }">
                    <BittsAvatar
                      size="x-small"
                      shape="square"
                      :is-entity="true"
                      :custom-svg="
                        item.integrationIcon
                          ? `${item.integrationIcon}Icon`
                          : ''
                      "
                    />
                  </template>
                  <template #prefix="{ option }">
                    <BittsAvatar
                      size="x-small"
                      shape="square"
                      :is-entity="true"
                      :custom-svg="
                        option.integrationIcon
                          ? `${option.integrationIcon}Icon`
                          : ''
                      "
                    />
                  </template>
                </BittsFilterMultiselect>
              </div>
            </div>
          </div>
          <!-- Single Sharing Level Section -->
          <div
            class="c-population-sharing-modal__section"
            :class="{
              'opacity-50': !canEdit || !hasItemsSelected,
            }"
          >
            <div class="c-population-sharing-modal__section-left">
              <h3 class="c-population-sharing-modal__section-title">
                Sharing level
              </h3>
              <p class="c-population-sharing-modal__section-description">
                {{ sharingLevelDescription }}
              </p>
            </div>
            <div class="c-population-sharing-modal__section-right">
              <SharingLevelCard
                v-model="globalVisibility"
                :show-greenfield="true"
                :disabled="!canEdit || !hasItemsSelected"
                :is-greenfield-blocked="isGreenfieldSharingBlocked"
              />
            </div>
          </div>

          <!-- Field Presets Section -->
          <div
            v-if="shouldShowPresetSelectors"
            class="c-population-sharing-modal__section"
          >
            <div class="c-population-sharing-modal__section-left">
              <h3 class="c-population-sharing-modal__section-title">
                {{ sharedFieldsTitle }}
              </h3>
              <p class="c-population-sharing-modal__section-description">
                {{ sharedFieldsDescription }}
              </p>
            </div>
            <div class="c-population-sharing-modal__section-right">
              <!-- Group populations by data source -->
              <template
                v-for="(group, feedKey, index) in populationsByDataSource"
                :key="feedKey"
              >
                <div class="c-population-sharing-modal__data-source-group">
                  <!-- Population list for this data source -->
                  <div
                    class="c-population-sharing-modal__populations-container"
                  >
                    <div class="c-population-sharing-modal__populations-list">
                      <div
                        v-for="population in getVisiblePopulations(
                          group,
                          feedKey as string,
                        )"
                        :key="population.id"
                        class="c-population-sharing-modal__population-item"
                      >
                        <BittsAvatar
                          size="x-small"
                          shape="square"
                          :is-entity="true"
                          :custom-svg="
                            population.integration_type
                              ? `${population.integration_type}Icon`
                              : ''
                          "
                        />
                        <span
                          class="c-population-sharing-modal__population-name"
                        >
                          {{ population.name }}
                        </span>
                        <BittsTag
                          v-if="population.standard_type"
                          size="x-small"
                          color="info"
                          text="Standard"
                        />
                      </div>
                      <!-- Show more/less button -->
                      <button
                        v-if="shouldShowExpandButton(group)"
                        type="button"
                        class="c-population-sharing-modal__show-more-button"
                        @click="
                          isFullyExpanded(group, feedKey as string)
                            ? collapsePopulationsForGroup(feedKey as string)
                            : expandPopulationsForGroup(feedKey as string)
                        "
                      >
                        <template
                          v-if="isFullyExpanded(group, feedKey as string)"
                        >
                          <div class="flex items-center gap-4">
                            <span>Show less</span>
                            <FontAwesomeIcon
                              :icon="['fak', 'chevron-up']"
                              class="text-secondary-accent"
                              :style="{ height: '16px', width: '16px' }"
                            />
                          </div>
                        </template>
                        <template v-else>
                          <div class="flex items-center gap-4">
                            <span>
                              Show
                              {{
                                Math.min(
                                  3,
                                  getRemainingCount(group, feedKey as string),
                                )
                              }}
                              more
                              {{
                                getRemainingCount(group, feedKey as string) ===
                                1
                                  ? 'population'
                                  : 'populations'
                              }}
                            </span>
                            <FontAwesomeIcon
                              :icon="['fak', 'chevron-down']"
                              class="text-secondary-accent"
                              :style="{ height: '16px', width: '16px' }"
                            />
                          </div>
                        </template>
                      </button>
                    </div>
                  </div>

                  <!-- Preset selector for this data source group -->
                  <SharingSettingsPresetSelector
                    :initial-selected-preset-id="group.displayPresetId"
                    :preset-options="group.presetOptions"
                    :feed-id="group.feedId"
                    :source-id="group.sourceId"
                    :show-tooltip-on-selected="true"
                    :can-edit-presets="canEdit"
                    :disabled="!canEdit"
                    @change="
                      (presetId) =>
                        handleDataSourcePresetChange(
                          group.populations,
                          presetId as number | null,
                        )
                    "
                    @open-preset-modal="
                      (presetId) =>
                        handleOpenPresetModal(
                          group.feedId,
                          group.sourceId,
                          presetId,
                        )
                    "
                  />
                </div>
                <BittsDivider
                  v-if="index < Object.keys(populationsByDataSource).length - 1"
                  class="my-16 opacity-50"
                />
              </template>
            </div>
          </div>
        </div>
      </div>
    </BittsLoading>
  </BittsModal>
</template>

<script setup lang="ts">
import {
  BittsAlert,
  BittsAvatar,
  BittsButton,
  BittsDivider,
  BittsFilterMultiselect,
  BittsLoading,
  BittsModal,
  BittsTag,
} from '@crossbeam/bitts';

import { onBeforeMount, ref } from 'vue';

import SharingSettingsPresetSelector from '@/components/sharing-settings/SharingSettingsPresetSelector.vue';

import SharingLevelCard from './SharingLevelCard.vue';
import { usePopulationSharingModal } from './usePopulationSharingModal';

const props = withDefaults(
  defineProps<{
    isForPartner?: boolean;
    isNewPopulation?: boolean;
    isMultiSelect?: boolean;
  }>(),
  {
    isForPartner: false,
    isNewPopulation: false,
    isMultiSelect: false,
  },
);

const emit = defineEmits(['close', 'saved']);

const selectedPopulationIds = defineModel<number[]>('selectedPopulationIds', {
  required: true,
});
const selectedPartnerIds = defineModel<number[]>('selectedPartnerIds', {
  default: () => [],
});

const isLoading = ref(true);

const {
  globalVisibility,
  isSaving,
  modalTitle,
  shouldShowAlert,
  alertMessage,
  alertDescription,
  sharingLevelDescription,
  sharedFieldsTitle,
  sharedFieldsDescription,
  populationsByDataSource,
  shouldShowPresetSelectors,
  hasChanges,
  hasItemsSelected,
  canEdit,
  isGreenfieldSharingBlocked,
  allPopulationOptions,
  allPartnerOptions,
  handleDataSourcePresetChange,
  handleOpenPresetModal,
  getVisiblePopulations,
  getRemainingCount,
  isFullyExpanded,
  shouldShowExpandButton,
  expandPopulationsForGroup,
  collapsePopulationsForGroup,
  handleSave,
  initialize,
} = usePopulationSharingModal(
  {
    selectedPopulationIds,
    selectedPartnerIds,
    isForPartner: props.isForPartner,
    isNewPopulation: props.isNewPopulation,
  },
  (event) => emit(event),
);

onBeforeMount(async () => {
  await initialize();
  isLoading.value = false;
});
</script>

<style scoped lang="pcss">
@reference "../../../../css/theme.css";

:deep(.c-population-sharing-modal-container) {
  .o-bitts-modal__header {
    @apply flex! justify-between! items-center! p-16! border-b! border-neutral-border! bg-white! sticky! top-0! z-20! flex-none!;
  }
  .o-bitts-modal__content {
    @apply !m-0 !p-0 min-h-[400px] max-h-[80vh];
  }
  .o-bitts-modal__footer {
    @apply gap-8! sticky! bottom-0! bg-white! z-20!;
    button:first-child {
      @apply ml-auto;
    }
  }
}

.c-population-sharing-modal__wrapper {
  @apply flex flex-col;
  height: min(600px, 70vh);
}

.c-population-sharing-modal__alert {
  @apply shrink-0 mb-10;
}

.c-population-sharing-modal__selection-section {
  @apply relative z-10 shrink-0;
}

.c-population-sharing-modal {
  @apply flex flex-col gap-8 overflow-y-auto p-16 flex-1 min-h-0;
}

.c-population-sharing-modal__section {
  @apply flex gap-40 p-16 rounded-8 bg-neutral-background-weak;
}

.c-population-sharing-modal__section-left {
  @apply flex flex-col gap-2 flex-shrink-0 w-[280px] self-start sticky top-0;
}

.c-population-sharing-modal__section-right {
  @apply flex flex-col flex-1 min-w-0;

  .c-population-sharing-modal__filter-multiselect {
    :deep(.bitts-filter-multiselect__trigger) {
      @apply min-h-40;
    }
    :deep(.bitts-filter-multiselect__selected-items .ant-tag) {
      @apply flex items-center gap-4 h-24 rounded-4 px-4 py-0;
    }
  }
}

.c-population-sharing-modal__section-title {
  @apply text-base font-bold text-neutral-text-strong;
}

.c-population-sharing-modal__section-description {
  @apply text-neutral-text-weak;
}

.c-population-sharing-modal__data-source-group {
  @apply flex flex-col gap-12;
}

.c-population-sharing-modal__populations-container {
  @apply flex flex-col gap-12;
}

.c-population-sharing-modal__populations-list {
  @apply flex flex-col gap-4;
}

.c-population-sharing-modal__population-item {
  @apply flex items-center gap-8;
}

.c-population-sharing-modal__population-name {
  @apply text-neutral-text-weak truncate;
}

.c-population-sharing-modal__show-more-button {
  @apply text-secondary-text cursor-pointer bg-transparent border-none p-0 text-left;
}

.c-population-sharing-modal__data-source-group {
  :deep(.c-preset-fields-tooltip) {
    @apply w-[240px];
  }

  :deep(.c-sharing-settings-preset-selector__preset) {
    @apply w-[240px] max-w-[240px];
  }
}
</style>
